import { AnimatePresence } from 'framer-motion'
import { useMemo, useState, type CSSProperties } from 'react'
import type { Category } from '../../lib/supabase'
import type { CatalogEntry } from '../../lib/catalog'
import { browseGroups } from '../../lib/allItems'
import { SheetPanel } from '../SheetPanel'
import { CheckIcon, CloseIcon } from '../icons'
import { SearchField } from '../ui/SearchField'
import { Highlight } from '../ui/Highlight'

// All items (opened from quick add's grid button, Plus): everything the
// household bought before and hasn't on the list, by aisle (lib/allItems.ts),
// to scroll through and tick several; "Add n to list" puts them all on at
// once, each in the category it went in last time, with one Undo
// (GroceryList). Ticks survive a search, so a few searches build one batch.

export function AllItemsSheet({ open, ...props }: {
  open: boolean
  /** Names used before and not on the list now (useItemHistory). */
  catalog: CatalogEntry[]
  categories: Category[]
  locale: string
  tintFor: (categoryId: string | null) => string
  onAdd: (entries: CatalogEntry[]) => void
  onClose: () => void
}) {
  return (
    <AnimatePresence>
      {open && (
        <SheetPanel onClose={props.onClose} ariaLabel="All items" maxHeight="92dvh" layout="column">
          <AllItemsBody {...props} />
        </SheetPanel>
      )}
    </AnimatePresence>
  )
}

function AllItemsBody({ catalog, categories, locale, tintFor, onAdd, onClose }: Omit<Parameters<typeof AllItemsSheet>[0], 'open'>) {
  const [query, setQuery] = useState('')
  const [ticked, setTicked] = useState<string[]>([])
  const groups = useMemo(() => browseGroups(catalog, categories, query, locale), [catalog, categories, query, locale])
  const toggle = (key: string) => setTicked(t => (t.includes(key) ? t.filter(k => k !== key) : [...t, key]))
  const byKey = useMemo(() => new Map(catalog.map(e => [e.key, e])), [catalog])
  const chosen = ticked.map(k => byKey.get(k)).filter((e): e is CatalogEntry => !!e)

  return (
    <div className="ml-browse">
      <header className="ml-browse__head">
        <div>
          <h2 className="ml-sheet-title ml-browse__title">All items</h2>
          <p className="ml-caption">{catalog.length} not on the list</p>
        </div>
        <button type="button" className="ml-browse__close" aria-label="Close" onClick={onClose}><CloseIcon size={20} /></button>
      </header>
      {catalog.length > 0 && (
        <SearchField className="ml-browse__search" label="Search all items" value={query} onChange={setQuery} />
      )}
      <div className="ml-browse__list">
        {catalog.length === 0 && (
          <p className="ml-caption ml-browse__empty">Finish a trip and what you bought shows up here, ready to add again.</p>
        )}
        {catalog.length > 0 && groups.length === 0 && (
          <p className="ml-caption ml-browse__empty">Nothing called “{query.trim()}” yet</p>
        )}
        {groups.map(g => {
          const name = g.category?.name ?? 'Other'
          return (
            <section key={g.category?.id ?? 'other'} role="group" aria-label={name} className="ml-browse__group"
              style={{ '--ml-tint': tintFor(g.category?.id ?? null) } as CSSProperties}>
              <h3 className="ml-label ml-browse__cat"><span className="ml-dot" aria-hidden="true" />{name}</h3>
              <ul>
                {g.entries.map(e => {
                  const on = ticked.includes(e.key)
                  return (
                    <li key={e.key}>
                      <button type="button" role="checkbox" aria-checked={on} aria-label={e.name}
                        className={`ml-browse__row${on ? ' is-on' : ''}`} onClick={() => toggle(e.key)}>
                        <span className="ml-browse__name"><Highlight text={e.name} query={query} /></span>
                        <span className="ml-browse__tick" aria-hidden="true">{on && <CheckIcon size={16} />}</span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </section>
          )
        })}
      </div>
      <footer className="ml-browse__foot">
        {chosen.length > 0 && (
          <button type="button" className="ml-btn ml-btn--secondary ml-browse__clear" onClick={() => setTicked([])}>Clear</button>
        )}
        <button type="button" className="ml-btn ml-btn--primary ml-browse__add" disabled={chosen.length === 0}
          onClick={() => onAdd(chosen)}>
          {chosen.length ? `Add ${chosen.length} to list` : 'Add to list'}
        </button>
      </footer>
    </div>
  )
}
